import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChatViewer, ChatViewerHandle } from 'chat-viewer';

import { Toast } from '@wix/wix-ui-tpa/cssVars';
import { ToastSkin } from '@wix/wix-ui-tpa';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  MessageType,
  Sender,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import {
  messagesModel,
  addMessage,
  selectMessages,
  selectOneChatComposed,
  addUploadFileMessage,
  AddMessagePayload,
  messagesSlice,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { A11Y_CHAT_DIALOG_ID } from '@/constants';
import {
  formatMessageBatchDateLabel,
  isSameLocalCalendarDay,
  isTodayMessageBatch,
} from '@/utils/formatMessageBatchDateLabel';
import { usePanoramaTransaction } from '@/hooks';
import { PanoramaTransactions } from '@/panoramaTransactions';

import { useOnEditorSettingsEvent } from 'AiAssistantWidget/Widget/contexts';
import { settingsEvents } from 'AiAssistantWidget/settingsEvents';

import { Container } from '../Container';
import { Header } from '../Header';
import { ChatMessage } from '../ChatMessage';
import { Loader } from '../Loader';
import { InputOneChat } from '../Input/InputOneChat';

import { DateDivider } from './DateDivider';
import { getActiveUnreadDividerBeforeMessageId } from './getActiveUnreadDividerBeforeMessageId';
import { NewMessagesDivider } from './NewMessagesDivider';
import { ScrollToNewMessagesButton } from './ScrollToNewMessagesButton';
import { useChatPendingNewMessages } from './useChatPendingNewMessages';
import { st, classes, cssStates } from './Chat.st.css';
import { useHistoryLoader } from './useHistoryLoader';
import { useOneChatIntroSequence } from './useOneChatIntroSequence';

export const OneChat: React.FC = () => {
  const { t, i18n } = useTranslation();
  const chatRef = useRef<ChatViewerHandle<messagesModel.Message>>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages.visibleMessages);
  const isTyping = useAppSelector(selectMessages.isTyping);
  const isLoaded = useAppSelector(selectMessages.isLoaded);
  const isLoading = useAppSelector(selectMessages.isLoading);
  const loadingFirstPage = useAppSelector(selectMessages.loadingFirstPage);
  const messagesIdle = useAppSelector(selectMessages.isIdle);
  const firstUnreadMessageId = useAppSelector(
    selectMessages.firstUnreadMessageId,
  );
  const shouldAddContactForm = useAppSelector(
    selectOneChatComposed.shouldSendContactFormAfterFirstMessage,
  );
  const loadOlderMessages = useHistoryLoader();
  const { isEditor } = useEnvironment();

  usePanoramaTransaction(
    PanoramaTransactions.MESSAGES_LOAD_VIEWER,
    isLoaded || isEditor,
  );
  const [uploadFileErrorToastText, setUploadFileErrorToastText] = useState<
    string | null
  >(null);

  const {
    pendingCount,
    showScrollToNewMessagesButton,
    dividerBeforeMessageId,
    onNewerMessages,
    onAtBottom,
    onScrollPositionChange,
    scrollToBottomAndClear,
  } = useChatPendingNewMessages(isEditor);

  const isBusy = useMemo(
    () => isTyping || !messagesIdle,
    [isTyping, messagesIdle],
  );

  const { isSequenceFulfilled } = useOneChatIntroSequence();

  const [value, setValue] = React.useState('');

  const onInputChange = useCallback((newValue: string) => {
    setValue(newValue);
    chatRef.current?.scrollToIndex(-1, { align: 'end', smooth: true });
  }, []);

  const onUploadFile = useCallback(
    ({ file, error }: { file?: File; error?: Error }) => {
      if (error || !file) {
        setUploadFileErrorToastText(
          t(error?.message ?? 'app.widget.UploadFileError'),
        );
        return;
      }

      dispatch(
        addUploadFileMessage({
          body: {
            mainText: '',
            additionalData: [{ file, type: 'file_uploaded' }],
          },
        }),
      );
    },
    [dispatch, t],
  );

  const onSubmit = useCallback(() => {
    if (value.trim().length) {
      const message: AddMessagePayload[] = [{ body: { mainText: value } }];

      if (shouldAddContactForm) {
        message.push({
          body: {
            mainText: t('app.widget.form.beforeText'),
          },
          sender: Sender.ASSISTANT,
          messageType: MessageType.CONTACT_FORM,
        });
      }
      dispatch(addMessage(message));
      setValue('');
    }
  }, [dispatch, value, shouldAddContactForm, t]);

  useOnEditorSettingsEvent(
    settingsEvents.openAssistantMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      chatRef.current?.scrollToIndex(1, { align: 'start', smooth: true });
    }, [isEditor]),
  );

  useEffect(() => {
    if (isEditor) {
      chatRef.current?.scrollToIndex(0, { align: 'end', smooth: true });
    }
  }, [isEditor, messages]);

  useEffect(
    () => () => {
      dispatch(messagesSlice.actions.clearFirstUnreadMessageId());
    },
    [dispatch],
  );

  const dateDividerLabels = useMemo(() => {
    const labelsByMessageId = new Map<string, string>();

    messages.forEach((message, index) => {
      const createdDate = message.createdDate;

      if (createdDate === undefined || createdDate <= 0) {
        return;
      }

      const previousCreatedDate = messages[index - 1]?.createdDate;
      const shouldShowDateDivider =
        previousCreatedDate === undefined ||
        previousCreatedDate <= 0 ||
        !isSameLocalCalendarDay(previousCreatedDate, createdDate);

      if (!shouldShowDateDivider) {
        return;
      }

      const label = formatMessageBatchDateLabel(createdDate, i18n.language);

      if (label) {
        labelsByMessageId.set(String(message.id), label);
      }
    });

    return labelsByMessageId;
  }, [messages, i18n.language]);

  const onScroll = useCallback(
    (scrollOffset: number) => {
      const chat = chatRef.current;

      if (!chat || isEditor) {
        return;
      }

      onScrollPositionChange(chat, scrollOffset);
    },
    [isEditor, onScrollPositionChange],
  );

  const renderMessage = useCallback(
    (message: messagesModel.Message) => {
      const activeUnreadDividerBeforeMessageId =
        getActiveUnreadDividerBeforeMessageId({
          dividerBeforeMessageId,
          firstUnreadMessageId,
        });
      const dateDividerLabel = dateDividerLabels.get(String(message.id));
      const showUnreadDivider =
        activeUnreadDividerBeforeMessageId !== null &&
        String(message.id) === String(activeUnreadDividerBeforeMessageId);
      const shouldMergeDateIntoUnreadDivider =
        showUnreadDivider &&
        Boolean(dateDividerLabel) &&
        isTodayMessageBatch(message.createdDate ?? 0);
      const unreadDividerLabel = shouldMergeDateIntoUnreadDivider
        ? t('app.widget.chat.newMessagesDivider')
        : undefined;

      return (
        <div key={message.id}>
          {dateDividerLabel && !shouldMergeDateIntoUnreadDivider ? (
            <DateDivider
              label={dateDividerLabel}
              createdDate={message.createdDate}
            />
          ) : null}
          {showUnreadDivider && (
            <NewMessagesDivider
              label={unreadDividerLabel}
              dateLabel={dateDividerLabel}
            />
          )}
          <ChatMessage
            message={message}
            last={message.id === messages.at(-1)?.id}
          />
        </div>
      );
    },
    [
      messages,
      dateDividerLabels,
      dividerBeforeMessageId,
      firstUnreadMessageId,
      t,
    ],
  );
  return (
    <Container ariaBusy={isBusy} dialogId={A11Y_CHAT_DIALOG_ID}>
      <Header dialogId={A11Y_CHAT_DIALOG_ID} />
      <div ref={parentRef} className={st(classes.container)}>
        <Toast
          isShown={!!uploadFileErrorToastText}
          shouldShowCloseButton
          skin={ToastSkin.error}
          className={st(classes.uploadFileToastError)}
          children={uploadFileErrorToastText}
          onClose={() => setUploadFileErrorToastText(null)}
        />
        {loadingFirstPage && <Loader />}
        <div
          className={st(classes.wrapper, cssStates({ isVisible: isLoaded }))}
          role="log"
          aria-label={t('app.widget.ariallabel.chatLog')}
          tabIndex={0}
        >
          <ChatViewer<messagesModel.Message>
            ref={chatRef}
            alignment="top"
            className={classes.chatViewer}
            scrollerClassName={classes.scroller}
            overscan={10}
            historyEndOffset={5}
            onHistoryEndReached={loadOlderMessages}
            onAtBottom={onAtBottom}
            onScroll={onScroll}
            prefix={isLoading && !isEditor && <Loader />}
            suffix={isTyping && !isEditor && <Loader />}
            messages={messages}
            renderMessage={renderMessage}
            onNewerMessages={onNewerMessages}
          />
          {!isEditor && (
            <ScrollToNewMessagesButton
              visible={showScrollToNewMessagesButton}
              count={pendingCount}
              chatRef={chatRef}
              onScrollToBottom={scrollToBottomAndClear}
            />
          )}
        </div>
      </div>
      <InputOneChat
        value={value}
        onChange={onInputChange}
        onSubmit={onSubmit}
        disabled={!isSequenceFulfilled}
        onUploadFile={onUploadFile}
      />
    </Container>
  );
};
